/**
 * Kontakt-Label Farb- und Anzeige-Konfiguration (Issue #573)
 * Passend zu den 14 Kategorien aus api/kontakt-labels.ts.
 */

export type KontaktLabel =
  | 'auftraggeber'
  | 'versicherung'
  | 'anwalt'
  | 'werkstatt'
  | 'lieferant'
  | 'mitarbeiter'
  | 'bank_finanzpartner'
  | 'versicherungsmakler'
  | 'sv_kollege'
  | 'behoerde'
  | 'adac_gdv'
  | 'kollege_intern'
  | 'vip'
  | 'do_not_contact';

export interface LabelColor {
  /** Badge-Hintergrund */
  bg: string;
  /** Badge-Text */
  text: string;
  /** Deutsch für Anzeige */
  label: string;
}

/** rgba bg + dark accessible text — used by LabelPicker and KontaktePage */
export const KONTAKT_LABEL_COLORS: Record<KontaktLabel, LabelColor> = {
  auftraggeber:       { bg: 'rgba(37,99,235,0.12)',   text: '#1E40AF', label: 'Auftraggeber' },
  versicherung:       { bg: 'rgba(124,58,237,0.12)',  text: '#5B21B6', label: 'Versicherung' },
  anwalt:             { bg: 'rgba(153,27,27,0.12)',   text: '#7F1D1D', label: 'Anwalt' },
  werkstatt:          { bg: 'rgba(217,119,6,0.12)',   text: '#92400E', label: 'Werkstatt' },
  lieferant:          { bg: 'rgba(107,114,128,0.12)', text: '#374151', label: 'Lieferant' },
  mitarbeiter:        { bg: 'rgba(5,150,105,0.12)',   text: '#065F46', label: 'Mitarbeiter' },
  bank_finanzpartner: { bg: 'rgba(202,138,4,0.12)',   text: '#78350F', label: 'Bank/Finanzpartner' },
  versicherungsmakler:{ bg: 'rgba(168,85,247,0.12)',  text: '#6B21A8', label: 'Versicherungsmakler' },
  sv_kollege:         { bg: 'rgba(30,58,138,0.12)',   text: '#1E3A8A', label: 'SV-Kollege' },
  behoerde:           { bg: 'rgba(55,65,81,0.12)',    text: '#1F2937', label: 'Behörde' },
  adac_gdv:           { bg: 'rgba(220,38,38,0.12)',   text: '#991B1B', label: 'ADAC/GDV' },
  kollege_intern:     { bg: 'rgba(22,163,74,0.12)',   text: '#14532D', label: 'Kollege intern' },
  vip:                { bg: 'rgba(180,83,9,0.12)',    text: '#7C2D12', label: 'VIP' },
  do_not_contact:     { bg: 'rgba(31,41,55,0.12)',    text: '#111827', label: 'Kein Kontakt' },
};

export const ALL_KONTAKT_LABELS: KontaktLabel[] = Object.keys(KONTAKT_LABEL_COLORS) as KontaktLabel[];

/** hex primary color + solid bg — used by KontaktDetailPage for inline badge styling */
export interface LabelConfig {
  label: string;
  color: string;
  bg: string;
}

const LABEL_HEX_COLORS: Record<KontaktLabel, Omit<LabelConfig, 'label'>> = {
  auftraggeber:       { color: '#2563EB', bg: '#EFF6FF' },
  versicherung:       { color: '#7C3AED', bg: '#F5F3FF' },
  anwalt:             { color: '#DC2626', bg: '#FEF2F2' },
  werkstatt:          { color: '#0D9488', bg: '#F0FDFA' },
  lieferant:          { color: '#D97706', bg: '#FFFBEB' },
  mitarbeiter:        { color: '#059669', bg: '#ECFDF5' },
  bank_finanzpartner: { color: '#1D4ED8', bg: '#DBEAFE' },
  versicherungsmakler:{ color: '#6366F1', bg: '#EEF2FF' },
  sv_kollege:         { color: '#0891B2', bg: '#ECFEFF' },
  behoerde:           { color: '#4B5563', bg: '#F3F4F6' },
  adac_gdv:           { color: '#F59E0B', bg: '#FEF3C7' },
  kollege_intern:     { color: '#8B5CF6', bg: '#EDE9FE' },
  vip:                { color: '#B45309', bg: '#FEF3C7' },
  do_not_contact:     { color: '#EF4444', bg: '#FEE2E2' },
};

export const LABEL_CONFIG: Record<KontaktLabel, LabelConfig> = Object.fromEntries(
  ALL_KONTAKT_LABELS.map(key => [key, { label: KONTAKT_LABEL_COLORS[key].label, ...LABEL_HEX_COLORS[key] }])
) as Record<KontaktLabel, LabelConfig>;

export const BETRIEB_LABELS = {
  gutachter_koop: 'Gutachter Koop',
  lcs_gmbh:       'LCS GmbH',
  mietgo:         'MietGo',
  lenneglas:      'Lenneglas',
  unfallgiganten: 'Unfallgiganten',
  protex:         'Protex',
  airbnb:         'Airbnb',
} as const;

export type BetriebSchluessel = keyof typeof BETRIEB_LABELS;

export const ALL_BETRIEB_SCHLUESSEL: BetriebSchluessel[] = Object.keys(BETRIEB_LABELS) as BetriebSchluessel[];
